import React from 'react';
import ReactDOM from 'react-dom';

/**
 * Custom Confirmation Modal Component
 * Used for consistent confirmation dialogs across the app
 * 
 * Props:
 * - isOpen: boolean - whether modal is visible
 * - onClose: function - called when cancel/close is clicked
 * - onConfirm: function - called when confirm button is clicked
 * - title: string - modal title
 * - message: string | React.ReactNode - modal body content
 * - confirmText: string - text for confirm button (default: "Confirm")
 * - cancelText: string - text for cancel button (default: "Cancel")
 * - type: "warning" | "danger" | "success" | "info" | "refund" - style preset
 * - icon: string - emoji/icon to display (optional, uses type default)
 * - isProcessing: boolean - shows loading state on confirm button
 */
const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning",
  icon = null,
  isProcessing = false
}) => {
  if (!isOpen) return null;

  const typeStyles = {
    warning: {
      gradient: 'from-yellow-400 via-orange-500 to-amber-500',
      bgGlow: 'bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 dark:from-yellow-900/20 dark:via-orange-900/20 dark:to-amber-900/20',
      border: 'border-yellow-300 dark:border-yellow-600',
      confirmBtn: 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600',
      defaultIcon: '⚠️'
    },
    danger: {
      gradient: 'from-red-400 via-red-500 to-red-600',
      bgGlow: 'bg-gradient-to-br from-red-50 via-red-50 to-red-100 dark:from-red-900/20 dark:via-red-900/20 dark:to-red-800/20',
      border: 'border-red-300 dark:border-red-600',
      confirmBtn: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
      defaultIcon: '🗑️'
    },
    success: {
      gradient: 'from-green-400 via-green-500 to-emerald-500',
      bgGlow: 'bg-gradient-to-br from-green-50 via-green-50 to-emerald-50 dark:from-green-900/20 dark:via-green-900/20 dark:to-emerald-900/20',
      border: 'border-green-300 dark:border-green-600',
      confirmBtn: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
      defaultIcon: '✅'
    },
    info: {
      gradient: 'from-blue-400 via-blue-500 to-blue-600',
      bgGlow: 'bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 dark:from-blue-900/20 dark:via-blue-900/20 dark:to-blue-800/20',
      border: 'border-blue-300 dark:border-blue-600',
      confirmBtn: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      defaultIcon: 'ℹ️'
    },
    refund: {
      gradient: 'from-emerald-400 via-teal-500 to-cyan-500',
      bgGlow: 'bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-emerald-900/20 dark:via-teal-900/20 dark:to-cyan-900/20',
      border: 'border-emerald-300 dark:border-emerald-600',
      confirmBtn: 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600',
      defaultIcon: '💰'
    }
  };

  const style = typeStyles[type] || typeStyles.warning;
  const displayIcon = icon || style.defaultIcon;

  const modal = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn p-4">
      <div className="relative transform transition-all duration-300 ease-out animate-scaleIn">
        <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md mx-4 border-2 ${style.border} relative overflow-hidden`}>
          {/* Background glow */}
          <div className={`absolute inset-0 ${style.bgGlow} rounded-2xl opacity-50`}></div>
          
          {/* Content */}
          <div className="relative">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl bg-gradient-to-br ${style.gradient} text-white shadow-lg`}>
                {displayIcon}
              </div>
            </div>

            {/* Title */}
            <h3 className={`text-xl font-bold text-center mb-3 text-transparent bg-clip-text bg-gradient-to-r ${style.gradient}`}>
              {title}
            </h3>

            {/* Message */}
            <div className={`${style.bgGlow} rounded-xl p-4 border ${style.border} mb-6`}>
              {typeof message === 'string' ? (
                <p className="text-center text-gray-700 dark:text-gray-300">
                  {message}
                </p>
              ) : (
                <div className="text-center text-gray-700 dark:text-gray-300">
                  {message}
                </div>
              )}
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all disabled:opacity-50"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={isProcessing}
                className={`flex-1 px-4 py-3 ${style.confirmBtn} text-white rounded-xl font-medium transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-2`}
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Processing...
                  </>
                ) : confirmText}
              </button>
            </div>
          </div>

          {/* Decorative sparkles */}
          <div className="absolute top-3 right-3 text-yellow-400 text-lg animate-pulse">✨</div>
          <div className="absolute bottom-3 left-3 text-yellow-400 text-lg animate-pulse" style={{ animationDelay: '0.5s' }}>✨</div>
        </div>
      </div>
      
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.3s ease-out; }
      `}</style>
    </div>
  );
  return typeof document !== 'undefined'
    ? ReactDOM.createPortal(modal, document.body)
    : modal;
};

export default ConfirmationModal;
